<template>
  <form
    class="card-white font-poppins mb-48 grid grid-cols-2 gap-15 p-40 lg:-mt-78"
    novalidate
    @submit.prevent="submit(data)"
  >
    <input
      v-bind="field('RQI_Institution')"
      v-model="data.RQI_Institution"
      class="max-md:col-span-2"
      placeholder="Institution *"
      aria-label="Institution (required)"
      autocomplete="organization"
    />
    <input
      v-bind="field('RQI_PositionTitle')"
      v-model="data.RQI_PositionTitle"
      class="max-md:col-span-2"
      placeholder="Position/Title *"
      aria-label="Position/Title (required)"
      autocomplete="organization-title"
    />
    <input
      v-bind="field('RQI_FirstName')"
      v-model="data.RQI_FirstName"
      class="max-md:col-span-2"
      placeholder="First Name *"
      aria-label="First Name (required)"
      autocomplete="given-name"
    />
    <input
      v-bind="field('RQI_LastName')"
      v-model="data.RQI_LastName"
      class="max-md:col-span-2"
      placeholder="Last Name *"
      aria-label="Last Name (required)"
      autocomplete="family-name"
    />
    <input
      v-bind="field('RQI_Address')"
      v-model="data.RQI_Address"
      class="col-span-2"
      placeholder="Address *"
      aria-label="Address (required)"
      autocomplete="street-address"
    />
    <input
      v-bind="field('RQI_City')"
      v-model="data.RQI_City"
      class="max-md:col-span-2"
      placeholder="City *"
      aria-label="City (required)"
      autocomplete="address-level2"
    />
    <DropDownList
      v-bind="field('STA_Code')"
      v-model="data.STA_Code"
      class="max-md:col-span-2"
      text-field="text"
      value-field="value"
      placeholder="State *"
      aria-label="State (required)"
      :data-items="states"
    />
    <input
      v-bind="field('RQI_Zip')"
      v-model="data.RQI_Zip"
      class="max-md:col-span-2"
      placeholder="Zip *"
      aria-label="Zip (required)"
      autocomplete="postal-code"
    />
    <PhoneNumberInput
      v-bind="field('RQI_Phone')"
      v-model="data.RQI_Phone"
      class="max-md:col-span-2"
      :placeholder="data.RQI_ReplyByPhone ? 'Phone *' : 'Phone'"
      :aria-label="data.RQI_ReplyByPhone ? 'Phone (required)' : 'Phone'"
    />
    <input
      v-bind="field('RQI_Email')"
      v-model="data.RQI_Email"
      class="col-span-2"
      :placeholder="data.RQI_ReplyByEmail ? 'Email *' : 'Email'"
      :aria-label="data.RQI_ReplyByEmail ? 'Email (required)' : 'Email'"
      type="email"
      autocomplete="email"
    />
    <textarea
      v-bind="field('RQI_Comments')"
      v-model="data.RQI_Comments"
      class="col-span-2 h-108"
      placeholder="Comments or Questions"
      aria-label="Comments or Questions"
      maxlength="500"
    />
    <fieldset class="col-span-2 flex">
      <legend class="float-left basis-140" v-text="'Please reply by:'" />
      <div class="flex gap-15">
        <label class="flex items-center gap-5">
          <input
            v-bind="field('RQI_ReplyByPhone')"
            v-model="data.RQI_ReplyByPhone"
            type="checkbox"
          />
          <span v-text="'Phone'" />
        </label>
        <label class="flex items-center gap-5">
          <input
            v-bind="field('RQI_ReplyByEmail')"
            v-model="data.RQI_ReplyByEmail"
            type="checkbox"
          />
          <span v-text="'Email'" />
        </label>
      </div>
    </fieldset>
    <span
      v-if="errorMessage"
      role="alert"
      class="text-red text-14 col-span-2 -mb-15"
      v-text="errorMessage"
    />
    <button
      class="btn-outline-green my-15"
      type="submit"
      :disabled="submitting"
      v-text="submitting ? 'Sending...' : 'Send Message'"
    />
  </form>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import DropDownList from '@/components/inputs/DropDownList.vue';
import PhoneNumberInput from '@/components/inputs/PhoneNumberInput.vue';
import { useFormSubmission } from '@/scripts/utils/composables';
import { states } from '@sas/shared/constants';
import type { RequestInfo } from '@sas/shared/database/form';
import { requestInfoSchema } from '@sas/shared/schemas';

/// Data
const data = ref({} as RequestInfo);
/// Composed
const { submit, submitting, errorMessage, invalid, field } = useFormSubmission(
  '/forms/request-info',
  requestInfoSchema,
  onSubmit,
);

/// Watchers
watch(
  () => data.value.RQI_ReplyByPhone,
  () => {
    invalid.value.delete('RQI_ReplyByEmail');
    invalid.value.delete('RQI_Phone');
  },
);

watch(
  () => data.value.RQI_ReplyByEmail,
  () => {
    invalid.value.delete('RQI_ReplyByPhone');
    invalid.value.delete('RQI_Email');
  },
);

/// Methods
function onSubmit() {
  window.location.href = '/aboutus/requestinfo/thankyou';
}
</script>
